import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames/bind";
import { InboxOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Input, Modal, Upload, message } from "antd";
import { useRecruitment } from "../../../../context/RecruitmentContext";
import { actionEnsureRecruitmentChatSession } from "../action";
import {
  CV_MAX_BYTES,
  buildChatMessagesFromUploadPayload,
  executeRecruitmentCvUpload,
  isAllowedCvFile,
} from "../../../../utils/constants/uploadCvUtils";
import { markRecruitmentSessionActive } from "../../../../utils/helps";
import styles from "../ChatBotRecruiment.module.sass";
import { PHONE_PATTERN } from "../../../../utils/constants/config";
import {
  buildCandidatePayload,
  buildJobContextPayload,
  buildRecruitmentSessionKey,
  buildUserInfoPayload,
  mergeChatMessages,
  mergeSessionMessagesWithCampaignUi,
} from "../../../../utils/constants/chatMessageUtils";

const cx = classNames.bind(styles);
const { Dragger } = Upload;

const CandidateInfoModal = ({
  open,
  onClose,
  campaignTitle = "",
  campaign = null,
  campaignId = null,
  showCvUpload = false,
  targetPath = "/chat",
}) => {
  const {
    candidateInfo,
    startChatSession,
    setCandidateInfo,
    openChatCampaign,
    setMessagesForCampaign,
  } = useRecruitment();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [cvFileList, setCvFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        fullName: candidateInfo.fullName,
        email: candidateInfo.email,
        phone: candidateInfo.phone,
        consent: candidateInfo.consent,
      });
    } else {
      setCvFileList([]);
    }
  }, [open, candidateInfo, form]);

  const handleBeforeCvUpload = (file) => {
    if (!isAllowedCvFile(file)) {
      message.error("Chỉ chấp nhận file PDF, DOC, DOCX");
      return Upload.LIST_IGNORE;
    }

    if (file.size > CV_MAX_BYTES) {
      message.error("File vượt quá 50MB");
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleFinish = async (values) => {
    const info = {
      fullName: values.fullName?.trim() || "",
      email: values.email?.trim() || "",
      phone: values.phone?.trim() || "",
      consent: Boolean(values.consent),
    };

    const selectedCvFile = cvFileList[0]?.originFileObj ?? null;

    if (!showCvUpload || !selectedCvFile || !campaignId) {
      startChatSession(values);
      navigate(targetPath, { replace: true });
      return;
    }

    setSubmitting(true);
    try {
      setCandidateInfo(info, { resetTimestamp: true });
      openChatCampaign(campaignId);

      const sessionResponse = await actionEnsureRecruitmentChatSession({
        candidate: buildCandidatePayload(info),
        campaign_id: campaignId,
      });
      const sessionPayload = sessionResponse?.data;

      if (!sessionPayload?.success || !sessionPayload?.data) {
        throw new Error("Không thể khởi tạo phiên ứng tuyển");
      }

      const { session, messages: apiMessages, candidate } = sessionPayload.data;
      const resolvedCandidateId = candidate?.id ?? null;

      if (session?.id) {
        markRecruitmentSessionActive(campaignId);
      }

      const uploadContext = {
        userId: info.phone,
        jobContext: buildJobContextPayload(campaign),
        session: buildRecruitmentSessionKey(info.phone, campaignId),
        userInfo: buildUserInfoPayload(info),
        candidate: buildCandidatePayload(info),
      };

      const uploadResult = await executeRecruitmentCvUpload({
        file: selectedCvFile,
        campaignId,
        candidateId: resolvedCandidateId,
        uploadContext,
      });

      let nextMessages = mergeSessionMessagesWithCampaignUi(
        campaign,
        info.fullName,
        apiMessages,
      );
      const uploadMessages = buildChatMessagesFromUploadPayload(
        uploadResult.payload,
      );
      if (uploadMessages.length > 0) {
        nextMessages = mergeChatMessages(nextMessages, uploadMessages);
      }

      setMessagesForCampaign(campaignId, nextMessages);

      startChatSession(values);
      if (uploadResult.files?.length > 0) {
        setCandidateInfo({ ...info, cv_files: uploadResult.files });
      }
      message.success("Ứng tuyển thành công");
      navigate(targetPath, { replace: true });
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={520}
      // destroyOnClose={false}
      className={cx("candidateModal")}
      title={null}
      closable={!submitting}
    // maskClosable={!submitting}
    >
      <div className={cx("registerPanel", "modalRegisterPanel")}>
        <p className={cx("welcomeText")}>Chào mừng bạn đến với</p>
        <h1>Trợ lý tuyển dụng AI</h1>
        <p className={cx("formHint")}>
          {showCvUpload
            ? `Vui lòng điền thông tin để ứng tuyển${campaignTitle ? ` vị trí ${campaignTitle}` : ""}`
            : "Vui lòng điền thông tin để bắt đầu trò chuyện với trợ lý ảo tuyển dụng"}
        </p>
        <Form
          form={form}
          layout="vertical"
          className={cx("registerForm")}
          requiredMark
          onFinish={handleFinish}
        >
          <Form.Item
            label="Họ và tên"
            name="fullName"
            rules={[{ required: true, message: "Vui lòng nhập họ và tên" }]}
          >
            <Input placeholder="Họ và tên" disabled={submitting} />
          </Form.Item>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Vui lòng nhập email" },
              { type: "email", message: "Email không hợp lệ" },
            ]}
          >
            <Input type="email" placeholder="Email" disabled={submitting} />
          </Form.Item>
          <Form.Item
            label="Số điện thoại"
            name="phone"
            rules={[
              {
                required: true,
                pattern: PHONE_PATTERN,
                message: "Số điện thoại không đúng định dạng !",
              },
            ]}
          >
            <Input placeholder="Số điện thoại" disabled={submitting} maxLength={10} type="number" />
          </Form.Item>

          {showCvUpload && (
            <Form.Item label="Đính kèm CV">
              <Dragger
                accept=".pdf,.doc,.docx"
                maxCount={1}
                disabled={submitting}
                fileList={cvFileList}
                beforeUpload={handleBeforeCvUpload}
                onChange={({ fileList }) => setCvFileList(fileList)}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Kéo thả file vào đây để tải lên
                </p>
                <p>PDF, DOC, DOCX — tối đa 50MB</p>
              </Dragger>
            </Form.Item>
          )}

          <Form.Item
            name="consent"
            valuePropName="checked"
            className={cx("consentItem")}
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error("Bạn cần đồng ý để tiếp tục")),
              },
            ]}
          >
            <Checkbox disabled={submitting}>
              Tôi đồng ý cho công ty lưu trữ thông tin để hỗ trợ tuyển dụng
            </Checkbox>
          </Form.Item>
          <Form.Item className={cx("submitItem")}>
            <Button
              type="primary"
              htmlType="submit"
              block
              className={cx("startButton")}
              loading={submitting}
            >
              {showCvUpload ? "Ứng tuyển ngay" : "Bắt đầu trò chuyện"}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default CandidateInfoModal;
